class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('open_cart') && urlParams.get('open_cart') === 'true') {
      this.open();
      urlParams.delete('open_cart');
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
    }
  }

  connectedCallback() {
    // Écouter les mises à jour du panier pour réinitialiser les event listeners
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        // Réinitialiser les event listeners après une mise à jour de quantité
        setTimeout(() => {
          this.initializeVariantSelectors();
          this.initializeUpsellButtons();
          this.initializeEmptyCartButtons();
          this.initializePromoCode();
        }, 100);
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('open_cart') && urlParams.get('open_cart') === 'true') {
      this.open();
      urlParams.delete('open_cart');
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    
    // Nettoyer l'event listener pour les clics extérieurs
    if (this.variantClickOutsideHandler) {
      document.removeEventListener('click', this.variantClickOutsideHandler);
    }
    
    // Nettoyer l'event listener de délégation pour les variantes
    if (this.variantDelegateHandler) {
      this.removeEventListener('click', this.variantDelegateHandler);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Initialiser les event listeners
    this.initializeVariantSelectors();
    this.initializeUpsellButtons();
    this.initializeEmptyCartButtons();
    this.initializePromoCode();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Utiliser requestAnimationFrame pour optimiser les manipulations DOM
    requestAnimationFrame(() => {
      this.classList.contains('is-empty') && this.classList.remove('is-empty');

      const sectionsToUpdate = [];

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        sectionsToUpdate.push({
          element: sectionElement,
          html: this.getSectionInnerHTML(parsedState.sections[section.id], section.selector),
        });
      });

      // Batch les mises à jour DOM
      sectionsToUpdate.forEach(({ element, html }) => {
        element.innerHTML = html;
      });

      // Utiliser requestIdleCallback pour différer les event listeners
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.initializeVariantSelectors();
          this.initializeUpsellButtons();
          this.initializeEmptyCartButtons();
          this.initializePromoCode();
          this.open();
        });
      } else {
        setTimeout(() => {
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.initializeVariantSelectors();
          this.initializeUpsellButtons();
          this.initializeEmptyCartButtons();
          this.initializePromoCode();

          this.open();
        }, 0);
      }
    });
  }

  initializeVariantSelectors() {
    // Supprimer l'ancien event listener de délégation s'il existe
    if (this.variantDelegateHandler) {
      this.removeEventListener('click', this.variantDelegateHandler);
    }

    // Créer un handler de délégation d'événements
    this.variantDelegateHandler = (event) => {
      // Vérifier si le clic est sur une option de variante
      const variantOption = event.target.closest('.cart-item__variant-option');
      
      if (variantOption) {
        const isUnavailable = variantOption.classList.contains('cart-item__variant-option--unavailable');
        
        if (!isUnavailable) {
          event.preventDefault();
          event.stopPropagation();
          this.handleVariantChange(event);
        }
      }
    };

    // Ajouter l'event listener de délégation sur le container principal
    this.addEventListener('click', this.variantDelegateHandler);

    // Ajouter l'écoute pour fermer les sélecteurs de variantes en cliquant à l'extérieur
    this.initializeVariantClickOutside();
  }

  initializeVariantClickOutside() {
    // Supprimer l'ancien event listener s'il existe
    if (this.variantClickOutsideHandler) {
      document.removeEventListener('click', this.variantClickOutsideHandler);
    }

    // Créer le nouveau handler
    this.variantClickOutsideHandler = (event) => {
      const variantDetails = this.querySelectorAll('.cart-item__variant-details[open]');

      variantDetails.forEach((details) => {
        const isClickInside = details.contains(event.target);
        if (!isClickInside) {
          details.removeAttribute('open');
        }
      });
    };

    // Ajouter l'event listener
    document.addEventListener('click', this.variantClickOutsideHandler);
  }

  initializeUpsellButtons() {
    const upsellButtons = this.querySelectorAll('.cart-drawer__upsell-products .quick-add__submit');
    upsellButtons.forEach((button) => {
      button.addEventListener('click', this.handleUpsellAdd.bind(this));
    });
  }

  initializeEmptyCartButtons() {
    const emptyCartButtons = this.querySelectorAll('.cart-drawer__empty-product-button');
    emptyCartButtons.forEach((button) => {
      button.addEventListener('click', this.handleEmptyCartAdd.bind(this));
    });
  }

  initializePromoCode() {
    const promoForm = this.querySelector('.cart-drawer__promo-form');
    if (promoForm) {
      promoForm.addEventListener('submit', this.handlePromoSubmit.bind(this));
    }
  }

  handlePromoSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const input = form.querySelector('#CartDrawer-PromoCode');
    const button = form.querySelector('.cart-drawer__promo-button');
    const successDiv = form.querySelector('.cart-drawer__promo-success');
    const errorDiv = form.querySelector('.cart-drawer__promo-error');
    const promoCode = input.value.trim();

    if (!promoCode) return;

    // Reset states
    this.resetPromoStates(successDiv, errorDiv);

    // Set loading state
    button.classList.add('loading');
    button.disabled = true;

    // Apply discount code
    fetch(`${routes.cart_url}/discount/${encodeURIComponent(promoCode)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.redirected) {
          // Discount applied successfully, reload cart
          return this.reloadCartDrawer();
        } else {
          throw new Error('Code promo invalide');
        }
      })
      .then(() => {
        // Show success state
        button.classList.remove('loading');
        button.classList.add('success');
        successDiv.querySelector('.cart-drawer__promo-success-text').textContent = `Code "${promoCode}" appliqué`;
        successDiv.style.display = 'flex';
        input.value = '';

        // Reset success state after 3 seconds
        setTimeout(() => {
          button.classList.remove('success');
          successDiv.style.display = 'none';
        }, 3000);
      })
      .catch((error) => {
        // Show error state
        errorDiv.textContent = error.message || 'Code promo invalide';
        errorDiv.style.display = 'block';

        // Reset error state after 5 seconds
        setTimeout(() => {
          errorDiv.style.display = 'none';
        }, 5000);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.disabled = false;
      });
  }

  resetPromoStates(successDiv, errorDiv) {
    successDiv.style.display = 'none';
    errorDiv.style.display = 'none';
  }

  reloadCartDrawer() {
    const sections = this.getSectionsToRender()
      .map((section) => section.id)
      .join('&section_id=');
    return fetch(`${routes.cart_url}?section_id=${sections}`)
      .then((response) => response.text())
      .then((html) => {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const newCartDrawer = parsedHTML.querySelector('cart-drawer');

        if (newCartDrawer) {
          // Mettre à jour les classes sur l'élément cart-drawer
          this.className = newCartDrawer.className;

          // Mettre à jour le contenu du cart drawer
          const newContent = newCartDrawer.querySelector('.drawer__inner');
          if (newContent) {
            this.querySelector('.drawer__inner').innerHTML = newContent.innerHTML;
            this.initializeVariantSelectors();
            this.initializeUpsellButtons();
            this.initializeEmptyCartButtons();
            this.initializePromoCode();
          }
        }

        // Mettre à jour la cart-icon-bubble
        const newCartBubble = parsedHTML.querySelector('#cart-icon-bubble');
        const currentCartBubble = document.querySelector('#cart-icon-bubble');
        if (newCartBubble && currentCartBubble) {
          currentCartBubble.innerHTML = newCartBubble.innerHTML;
        }
      });
  }

  handleVariantChange(event) {
    // Avec la délégation d'événements, on utilise closest pour trouver l'option
    const option = event.target.closest('.cart-item__variant-option');
    
    if (!option) return;
    
    const variantId = option.dataset.variantId;
    const itemKey = option.dataset.itemKey;
    const itemIndex = option.dataset.itemIndex;

    if (!variantId || !itemKey) return;

    // Fermer le détails (le sélecteur)
    const details = option.closest('details');
    if (details) {
      details.removeAttribute('open');
    }

    // Vérifier si c'est déjà la variante sélectionnée
    if (option.classList.contains('cart-item__variant-option--selected')) {
      return;
    }

    // Activer l'indicateur de chargement
    this.enableVariantLoading(itemIndex);

    // Envoyer la requête de changement de variante
    this.changeCartItemVariant(itemKey, variantId, itemIndex);
  }

  async changeCartItemVariant(itemKey, variantId, itemIndex) {
    try {
      // Récupérer la quantité actuelle de l'article
      const cartItem = document.querySelector(`[data-key="${itemKey}"]`);
      const scriptElement = cartItem?.querySelector('script[type="application/json"]');
      let currentQuantity = 1;

      if (scriptElement) {
        const itemData = JSON.parse(scriptElement.textContent);
        currentQuantity = itemData.quantity || 1;
      }

      // Étape 1: Supprimer l'ancienne variante
      const removeBody = JSON.stringify({
        id: itemKey,
        quantity: 0,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      });

      const removeResponse = await fetch(`${routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: removeBody,
      });

      if (!removeResponse.ok) {
        throw new Error("Erreur lors de la suppression de l'ancienne variante");
      }

      // Étape 2: Ajouter la nouvelle variante
      const addData = await this.addProductToCartWithSections(parseInt(variantId), currentQuantity);

      // Étape 3: Mettre à jour l'interface
      this.renderContents(addData);
    } catch (error) {
      console.error('Erreur lors du changement de variante:', error);
      this.reloadCartDrawer();
    } finally {
      // Désactiver l'indicateur de chargement
      this.disableVariantLoading(itemIndex);
    }
  }

  /**
   * Active l'indicateur de chargement pour un changement de variante
   * @param {string|number} itemIndex - Index de l'article
   */
  enableVariantLoading(itemIndex) {
    // Désactiver l'interaction avec le panier
    const mainCartItems = document.querySelector('#main-cart-items, #CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    // Afficher les spinners de chargement
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${itemIndex} .loading__spinner`);

    console.log('loading, element : ', cartDrawerItemElements);

    cartDrawerItemElements.forEach((spinner) => {
      spinner.classList.remove('hidden');
      spinner.parentElement.classList.add('loading');
    });

    // Enlever le focus actif
    document.activeElement.blur();
  }

  /**
   * Désactive l'indicateur de chargement pour un changement de variante
   * @param {string|number} itemIndex - Index de l'article
   */
  disableVariantLoading(itemIndex) {
    // Réactiver l'interaction avec le panier
    const mainCartItems = document.querySelector('#main-cart-items, #CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    // Masquer les spinners de chargement
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${itemIndex} .loading__spinner`);

    cartDrawerItemElements.forEach((spinner) => {
      spinner.classList.add('hidden');
      spinner.parentElement.classList.remove('loading');
    });
  }

  async handleUpsellAdd(event) {
    // Wellnove fix : ne PAS auto-ajouter si le bouton ouvre un modal (multi-variant)
    // → c'est le formulaire INTÉRIEUR de la popup qui gère l'ajout avec la variante choisie.
    const _btn = event && (event.currentTarget || event.target);
    if (_btn && _btn.closest && _btn.closest('modal-opener')) {
      return;
    }

    event.preventDefault();
    const button = event.currentTarget;

    // Désactiver le bouton et afficher le loading
    button.setAttribute('disabled', true);
    button.classList.add('loading');

    try {
      let variantId;

      // Vérifier s'il y a un formulaire (produit avec une seule variante)
      const form = button.closest('form');
      if (form) {
        const variantInput = form.querySelector('input[name="id"]');
        variantId = variantInput ? variantInput.value : null;
      } else {
        // Produit avec plusieurs variantes - utiliser la première variante disponible
        const productUrl = button.dataset.productUrl;
        if (productUrl) {
          const productHandle = productUrl.split('/').pop();
          const productData = await this.fetchProductData(productHandle);
          const firstAvailableVariant = productData.variants.find((variant) => variant.available);
          variantId = firstAvailableVariant ? firstAvailableVariant.id : null;
        }
      }

      if (!variantId) {
        throw new Error('Aucune variante disponible');
      }

      // Ajouter au panier avec les sections à mettre à jour
      const cartData = await this.addProductToCartWithSections(variantId, 1);

      // Utiliser renderContents pour une mise à jour complète
      this.renderContents(cartData);
    } catch (error) {
      console.error("Erreur lors de l'ajout du produit upsell:", error);
    } finally {
      button.removeAttribute('disabled');
      button.classList.remove('loading');
    }
  }

  async handleEmptyCartAdd(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const productId = button.dataset.productId;

    if (!productId) {
      console.error('Product ID manquant');
      return;
    }

    // Désactiver le bouton et afficher le loading
    this.setEmptyCartButtonLoading(button, true);

    try {
      // Récupérer les informations du produit
      const productData = await this.fetchProductData(productId);

      if (!productData || !productData.variants || productData.variants.length === 0) {
        throw new Error('Produit non disponible');
      }

      // Prendre la première variante disponible
      const firstAvailableVariant = productData.variants.find((variant) => variant.available);

      if (!firstAvailableVariant) {
        throw new Error('Aucune variante disponible');
      }

      // Ajouter au panier avec les sections à mettre à jour
      const cartData = await this.addProductToCartWithSections(firstAvailableVariant.id, 1);

      // Utiliser renderContents pour une mise à jour complète
      this.renderContents(cartData);
    } catch (error) {
      console.error("Erreur lors de l'ajout au panier:", error);
      this.showEmptyCartError(error.message);
    } finally {
      // Réactiver le bouton
      this.setEmptyCartButtonLoading(button, false);
    }
  }

  async fetchProductData(productId) {
    const response = await fetch(`/products/${productId}.js`);

    if (!response.ok) {
      throw new Error('Impossible de récupérer les données du produit');
    }

    return response.json();
  }

  async addProductToCart(variantId, quantity = 1) {
    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity,
        },
      ],
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Erreur lors de l'ajout au panier");
    }

    return response.json();
  }

  async addProductToCartWithSections(variantId, quantity = 1) {
    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity,
        },
      ],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Erreur lors de l'ajout au panier");
    }

    return response.json();
  }

  setEmptyCartButtonLoading(button, isLoading) {
    if (isLoading) {
      button.disabled = true;
      button.classList.add('loading');
      button.textContent = 'Ajout...';
    } else {
      button.disabled = false;
      button.classList.remove('loading');
      button.textContent = 'Ajouter';
    }
  }

  showEmptyCartError(message) {
    // Créer ou mettre à jour un élément d'erreur
    let errorElement = this.querySelector('.cart-empty-error');

    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'cart-empty-error';
      errorElement.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #f44336;
        color: white;
        padding: 12px 16px;
        border-radius: 4px;
        z-index: 1000;
        font-size: 14px;
      `;
      this.appendChild(errorElement);
    }

    errorElement.textContent = message;
    errorElement.style.display = 'block';

    // Masquer l'erreur après 5 secondes
    setTimeout(() => {
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }, 5000);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    console.log(new DOMParser().parseFromString(html, 'text/html'));
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// Fix: variant dropdown must escape overflow:auto on cart-drawer-items
document.addEventListener('toggle', (e) => {
  const details = e.target;
  if (!details.classList || !details.classList.contains('cart-item__variant-details')) return;
  const scrollContainer = details.closest('cart-drawer-items');
  if (!scrollContainer) return;
  if (details.open) {
    scrollContainer.style.overflow = 'visible';
  } else {
    scrollContainer.style.overflow = '';
  }
}, true);
